import { SearchOutlined } from '@ant-design/icons'
import { Row } from 'antd'
import KForm from 'app/components/Modules/Form/_KForm'
import KCheckbox from 'app/components/Modules/Form/KCheckbox'
import KInputText from 'app/components/Modules/Form/KInputText'
import KRadio from 'app/components/Modules/Form/KRadio'
import KReadOnlyInput from 'app/components/Modules/Form/KReadOnlyInput'
import KTextArea from 'app/components/Modules/Form/KTextArea'
import Restricted from 'app/components/UI-Elements/General/Restricted'
import KCol from 'app/components/UI-Elements/Layout/KCol'
import useTranslation from 'app/lib/hooks/useTranslation'
import { RecordTypeEnum } from 'app/queries'
import { Routes } from 'app/routes/Routes'
import { omit } from 'lodash'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import useDeliveryContactForm from '../../Checkout/hooks/useDeliveryContactForm'
import useManagerContactForm from '../../Checkout/hooks/useManagerContactForm'
import { MemberProps } from '../../convention'
import DeliveryAddressListItem from '../../DeliveryAddress/ListItem'
import useDeliveryAddressForm from '../hooks/useDeliveryAddressForm'

type Props = {
  portalId?: string
  onSuccess?: () => void
  loading?: boolean
}

const Form: React.FC<MemberProps<IRental, Props>> = ({
  member,
  props: { portalId, onSuccess, loading }
}) => {
  const navigate = useNavigate()
  const [showUpfront, setShowUpfront] = useState(false)
  const { tAttribute } = useTranslation({ recordType: RecordTypeEnum.rental })
  const { open: openAddressForm } = useDeliveryAddressForm(member.id.toString())

  const deliveryContact = useDeliveryContactForm()
  const managerContact = useManagerContactForm()

  useEffect(() => {
    onValuesChange({ deliveryMethod: getDefaultDelivery() })
  }, [member.deliveryMethod])

  const onValuesChange = (values: Pick<IRental, 'deliveryMethod'>) => {
    if ('deliveryMethod' in values) {
      setShowUpfront(values?.deliveryMethod === 'delivery')
    }
  }

  const onDeliveryContactFocus = () => {
    deliveryContact.open({ member: member, props: { onSuccess } })
  }

  const onAddressEdit = () => {
    openAddressForm({
      member: member,
      props: {
        formProps: { onSuccess },
        addressInputProps: { showAvailabilityHint: true }
      }
    })
  }

  const onManagerContactFocus = () => managerContact.open({ member: member, props: { onSuccess } })

  const onFormSuccess = () => {
    navigate(Routes.Checkout.member(String(member.id)).confirmation)
  }

  const getDefaultDelivery = () =>
    member?.deliveryMethod || member?.defaultDeliveryMethod || 'delivery'

  const labelRender = (label: string) => <h5 className="text-black mb-0">{label}</h5>

  return (
    <KForm
      mutationOptions={{
        silentSuccess: true,
        payloadNormalizer: (data: IRental) =>
          omit(data, 'addressSummary', 'deliveryContact.name', 'managerContact.name')
      }}
      recordType={RecordTypeEnum.rental}
      recordId={member.id}
      initialValues={{ ...member, deliveryMethod: getDefaultDelivery() }}
      initialFetch={false}
      portalId={portalId}
      onSuccess={onFormSuccess}
      onValuesChange={onValuesChange}
      layout="vertical"
      loading={loading}>
      <Row gutter={[0, 12]}>
        <KCol span={24}>{labelRender(tAttribute({ id: 'addressSummary' }))}</KCol>
        <KCol span={24}>
          <DeliveryAddressListItem
            member={member?.deliveryAddress}
            props={{ onEditClick: onAddressEdit }}
          />
        </KCol>
        <KCol span={24}>
          <KRadio optionType="button" name="deliveryMethod" labelRenderer={labelRender} />
        </KCol>
        <If condition={showUpfront}>
          <KCol span={24}>
            <KCheckbox name="upfrontDelivery" hasFeedback={false} />
          </KCol>
        </If>
        <KCol span={24}>
          <KInputText name="customerOrderNumber" />
        </KCol>
        <KCol span={24}>
          <KReadOnlyInput
            required
            name="deliveryContact.name"
            suffix={<SearchOutlined />}
            onClick={onDeliveryContactFocus}
            labelRenderer={labelRender}
            value={member?.deliveryContact?.name}
          />
          <KCheckbox name="receiveSmsUpdates" hasFeedback={false} />
        </KCol>
        <Restricted allowFor={['member']}>
          <KCol span={24}>
            <KReadOnlyInput
              required
              name="managerContact.name"
              suffix={<SearchOutlined />}
              onClick={onManagerContactFocus}
              labelRenderer={labelRender}
              value={member?.managerContact?.name}
            />
          </KCol>
        </Restricted>
        <KCol span={24}>
          <KTextArea name="annotation" />
        </KCol>
      </Row>
    </KForm>
  )
}

export default Form
